"use client";

import { c as _c } from "react/compiler-runtime";
import { graphql, readFragment } from "@opensea/graphql";
import { sortBy } from "es-toolkit/array";
import { set } from "es-toolkit/compat";
import { CurrencySparkLineChartFragment } from "@/components/currencies/charts/CurrencySparkLineChart/CurrencySparkLineChart.fragment";
import { currencyIdentifierFragment, getCurrencyIdentifier } from "@/components/currencies/utils";
import { Paginator } from "@/lib/pagination/paginator";
import { useSubscription } from "../use-subscription";
export const CurrencyStatsSubscriptionFragment = graphql(`
    fragment CurrencyStatsSubscription on Currency {
      id
      usdPrice
      stats {
        marketCapUsd
        fdvUsd
        totalSupply
        oneHour {
          priceChange
        }
        oneDay {
          volume
          priceChange
          marketCapChange
        }
        sevenDay {
          priceChange
        }
        fourteenDay {
          priceChange
        }
        thirtyDay {
          priceChange
        }
      }
      ...CurrencySparkLineChart
      ...currencyIdentifier
    }
  `, [CurrencySparkLineChartFragment, currencyIdentifierFragment]);
const subscription = graphql(`
    subscription useCurrencyStatsSubscription(
      $currencies: [ContractIdentifierInput!]
    ) {
      currencies(currencies: $currencies) {
        id
        ...CurrencyStatsSubscription
      }
    }
  `, [CurrencyStatsSubscriptionFragment]);
class CurrencyStatsPaginator extends Paginator {
  getValues(itemKey, sortBy) {
    const item = readFragment(CurrencyStatsSubscriptionFragment, itemKey);
    switch (sortBy) {
      case "ONE_DAY_VOLUME":
        return [item.stats?.oneDay.volume ?? 0, item.id];
      case "MARKET_CAP":
        return [item.stats?.marketCapUsd ?? 0, item.id];
      case "PRICE":
        return [item.usdPrice ?? 0, item.id];
      case "ONE_DAY_PRICE_CHANGE":
        return [item.stats?.oneDay.priceChange ?? 0, item.id];
      case "ONE_HOUR_PRICE_CHANGE":
        return [item.stats?.oneHour.priceChange ?? 0, item.id];
      case "SEVEN_DAY_PRICE_CHANGE":
        return [item.stats?.sevenDay.priceChange ?? 0, item.id];
      case "FOURTEEN_DAY_PRICE_CHANGE":
        return [item.stats?.fourteenDay.priceChange ?? 0, item.id];
      case "THIRTY_DAY_PRICE_CHANGE":
        return [item.stats?.thirtyDay.priceChange ?? 0, item.id];
      default:
        return [item.id];
    }
  }
}
const currencyStatsPaginator = new CurrencyStatsPaginator({
  // no limit ~ if we want to limit the table, we need to do it on the pagination level as well
  // to avoid infinite reloading
  maxItems: undefined
});
const MAX_CURRENCIES_TO_LIVE_UPDATE = 200;
export const useCurrenciesStatsSubscription = (items, setItems, sort) => {
  const $ = _c(12);
  let t0;
  let t1;
  let t2;
  if ($[0] !== items) {
    const identifiers = sortBy(items, ["id"]).map(getIdentifier).filter(isDefined).slice(0, MAX_CURRENCIES_TO_LIVE_UPDATE);
    t2 = useSubscription;
    t1 = subscription;
    t0 = identifiers.map(_temp);
    $[0] = items;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
  } else {
    t0 = $[1];
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t0) {
    t3 = {
      currencies: t0
    };
    $[4] = t0;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t3) {
    t4 = {
      query: t1,
      variables: t3
    };
    $[6] = t1;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== setItems || $[10] !== sort) {
    t5 = msg => {
      const updates = msg.currencies;
      if (!updates?.length) {
        return;
      }
      setItems(prev => {
        let next = prev;
        for (const update of updates) {
          next = currencyStatsPaginator.updateInOrder(next, sort, element => getId(element) === update.id, element_0 => {
            const updated = readFragment(CurrencyStatsSubscriptionFragment, update);
            return set(element_0, "usdPrice", updated.usdPrice);
          });
        }
        return next;
      });
    };
    $[9] = setItems;
    $[10] = sort;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  t2(t4, t5);
};
function getId(item) {
  // Currency items always have an id
  return item.id;
}
function isDefined(value) {
  return value !== undefined && value !== null;
}
const getIdentifier = item => {
  const {
    contractAddress,
    chainIdentifier
  } = getCurrencyIdentifier(readFragment(CurrencyStatsSubscriptionFragment, item));
  if (!(contractAddress && chainIdentifier)) {
    return undefined;
  }
  return {
    address: contractAddress,
    chain: chainIdentifier
  };
};
function _temp(i) {
  return {
    address: i.address,
    chain: i.chain
  };
}